import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { logoutUser } from '../services/auth'
import { getTutorCourses, getAllCourses, deleteCourse, getAllSections, getAllTutorsBasic, createSection, updateSection, deleteSection, updateUserRolesAndAdminStatus, disableUserAccount, deleteUserAccount } from '../services/firestore'
import { ROLE_KEYS, ROLE_LABELS } from '../services/accessKeys'
import {
  LogOut,
  Key,
  User,
  BookOpen,
  GraduationCap,
  Plus,
  Users,
  Clock,
  Settings,
  ArrowRight,
  Search,
  Home,
  TrendingUp,
  BarChart3,
  Calendar,
  Trash2,
  MapPin,
  MessageSquare,
  Image as ImageIcon,
  Link as LinkIcon,
  Upload,
  QrCode,
  Microscope,
  Edit,
  IdCard
} from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import ProfileEditor from './ProfileEditor'
import ResearchGroupsDashboard from '../components/ResearchGroupsDashboard'
import { createWallPost } from '../services/firestore'
import { storage } from '../firebase/config'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { capitalizeName } from '../utils/formatters'
import './Dashboard.css'

const DAYS_OF_WEEK = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

const COURSE_COLORS = [
  { bg: '#e0f2fe', border: '#0284c7', text: '#0369a1' }, // Light Blue
  { bg: '#dcfce7', border: '#16a34a', text: '#15803d' }, // Light Green
  { bg: '#fef9c3', border: '#ca8a04', text: '#a16207' }, // Light Yellow
  { bg: '#f3e8ff', border: '#9333ea', text: '#7e22ce' }, // Light Purple
  { bg: '#fee2e2', border: '#dc2626', text: '#b91c1c' }, // Light Red
  { bg: '#e0f7fa', border: '#00acc1', text: '#00838f' }, // Light Cyan
  { bg: '#ffebd5', border: '#ea580c', text: '#c2410c' }, // Light Orange
  { bg: '#fae8ff', border: '#d946ef', text: '#a21caf' }, // Light Pink
]

const getCourseColor = (courseId) => {
  if (!courseId) return { bg: '#f8fafc', border: '#cbd5e1', text: '#475569' }
  let hash = 0
  for (let i = 0; i < courseId.length; i++) {
    hash = courseId.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % COURSE_COLORS.length
  return COURSE_COLORS[index]
}

const getCurrentDayName = () => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
  return days[new Date().getDay()]
}

const Dashboard = () => {
  const { user, tutorProfile } = useAuth()
  const navigate = useNavigate()
  const [myCourses, setMyCourses] = useState([])
  const [allCourses, setAllCourses] = useState([])
  const [allSections, setAllSections] = useState([])
  const [allTutorsData, setAllTutorsData] = useState([])
  const [showWeekends, setShowWeekends] = useState(false)
  const [hasSetDefaultWeekends, setHasSetDefaultWeekends] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showJoinModal, setShowJoinModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const [showCalendarFormModal, setShowCalendarFormModal] = useState(false)
  const [editingSection, setEditingSection] = useState(null)
  const [calendarFormData, setCalendarFormData] = useState({
    courseId: '',
    day: '',
    date: '',
    startTime: '',
    endTime: '',
    building: '',
    classroom: '',
    meetingLink: '',
    topic: '',
    locationType: 'physical'
  })
  const [calendarError, setCalendarError] = useState('')

  const [showModerationModal, setShowModerationModal] = useState(false)
  const [moderatingUser, setModeratingUser] = useState(null)
  const [moderationFormData, setModerationFormData] = useState({
    tutorRoles: [],
    isAdmin: false,
    isAccredited: false,
    isRetired: false,
    retiredData: { startDate: '', endDate: '', honorBio: '' }
  })
  const [moderationError, setModerationError] = useState('')
  const [moderationSaving, setModerationSaving] = useState(false)

  const hasTutorRoles = tutorProfile?.tutorRoles && Array.isArray(tutorProfile.tutorRoles)
  const roles = hasTutorRoles
    ? tutorProfile.tutorRoles
    : (tutorProfile?.tutorType === 'eucs' ? ['eucs'] : (tutorProfile?.tutorType === 'aurora' ? ['aurora'] : (tutorProfile?.tutorType === 'phuma' ? ['phuma'] : ['vtic'])))
  const isVTIC = roles.includes('vtic') || (!hasTutorRoles && tutorProfile?.tutorType === 'standard')
  const isAdmin = tutorProfile?.isAdmin === true || user?.email === 'admin@vtic.com'
  const isNormalTutor = isVTIC || roles.includes('eucs') || (!hasTutorRoles && tutorProfile?.tutorType === 'eucs')
  const canEditCalendar = isVTIC || isAdmin
  const hasCalendarRole = roles.includes('aurora') || roles.includes('phuma') || (!hasTutorRoles && (tutorProfile?.tutorType === 'aurora' || tutorProfile?.tutorType === 'phuma'))

  const formatDateShort = (dateStr) => {
    if (!dateStr) return ''
    try {
      const [, month, day] = dateStr.split('-')
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      return `${parseInt(day)} ${months[parseInt(month) - 1]}`
    } catch {
      return dateStr
    }
  }
  const [activeCategory, setActiveCategory] = useState('tutorias') // tutorias, investigacion, usuario
  const [activeTab, setActiveTab] = useState('weekly') // 'weekly', 'dist', 'classes', 'profile', 'wall', 'qr', 'research_groups', 'carnet'
  const [isProfileDirty, setIsProfileDirty] = useState(false)
  const [showFullscreenCarnet, setShowFullscreenCarnet] = useState(false)

  const getInitials = (name) => {
    if (!name) return 'VTIC'
    const words = name.trim().split(/\s+/)
    if (words.length >= 3) {
      return (words[0][0] + words[1][0] + words[words.length - 1][0]).toUpperCase()
    } else if (words.length === 2) {
      return (words[0][0] + words[1][0]).toUpperCase()
    }
    return words[0].slice(0, 3).toUpperCase()
  }

  const handleCategoryChange = (cat) => {
    if (activeTab === 'profile' && isProfileDirty) {
      if (window.confirm('Tienes cambios sin guardar en tu perfil. ¿Estás seguro de que quieres salir?')) {
        setIsProfileDirty(false)
        executeCategoryChange(cat)
      }
    } else {
      executeCategoryChange(cat)
    }
  }

  const executeCategoryChange = (cat) => {
    setActiveCategory(cat)
    if (cat === 'tutorias') {
      if (isNormalTutor || isAdmin) {
        setActiveTab('weekly')
      } else {
        setActiveTab('calendar')
      }
    }
    if (cat === 'investigacion') setActiveTab('research_groups')
    if (cat === 'usuario') setActiveTab('profile')
    if (cat === 'administracion') setActiveTab('admin_users')
  }

  const handleTabChange = (tab) => {
    if (activeTab === 'profile' && isProfileDirty) {
      if (window.confirm('Tienes cambios sin guardar en tu perfil. ¿Estás seguro de que quieres salir?')) {
        setIsProfileDirty(false)
        setActiveTab(tab)
      }
    } else {
      setActiveTab(tab)
    }
  }

  // Wall post state
  const [postContent, setPostContent] = useState('')
  const [postLinkTitle, setPostLinkTitle] = useState('')
  const [postLinkUrl, setPostLinkUrl] = useState('')
  const [postImage, setPostImage] = useState(null)
  const [postImagePreview, setPostImagePreview] = useState(null)
  const [isPosting, setIsPosting] = useState(false)
  const [postError, setPostError] = useState('')
  const [postSuccess, setPostSuccess] = useState('')
  const fileInputRef = React.useRef(null)

  // Calcular estadísticas
  const totalSections = myCourses.reduce((acc, course) => {
    return acc + (course.sections?.length || 0)
  }, 0)

  const totalTutors = new Set(
    myCourses.flatMap(course => course.tutors || [])
  ).size

  // Horarios por día
  const sectionsByDay = myCourses.reduce((acc, course) => {
    course.sections?.forEach(section => {
      const day = section.day || 'Sin día'
      acc[day] = (acc[day] || 0) + 1
    })
    return acc
  }, {})

  useEffect(() => {
    loadCourses()
  }, [user])

  useEffect(() => {
    if (tutorProfile) {
      const hasRolesField = tutorProfile.tutorRoles && Array.isArray(tutorProfile.tutorRoles)
      const uRoles = hasRolesField
        ? tutorProfile.tutorRoles
        : (tutorProfile.tutorType === 'eucs' ? ['eucs'] : (tutorProfile.tutorType === 'aurora' ? ['aurora'] : (tutorProfile.tutorType === 'phuma' ? ['phuma'] : ['vtic'])))
      const userIsVTIC = uRoles.includes('vtic') || (!hasRolesField && tutorProfile.tutorType === 'standard')
      const userIsEUCS = uRoles.includes('eucs') || (!hasRolesField && tutorProfile.tutorType === 'eucs')
      const userIsAdmin = tutorProfile.isAdmin === true || user?.email === 'admin@vtic.com'
      const userIsNormal = userIsVTIC || userIsEUCS
      
      if (!userIsNormal && !userIsAdmin) {
        // Solo es Aurora o Phuma, no ve Tutorías ni Investigación: mandarlo a perfil
        setActiveCategory('usuario')
        setActiveTab('profile')
      }
    }
  }, [tutorProfile, user])

  const loadCourses = async () => {
    if (!user) return

    setLoading(true)
    try {
      // Cargar cursos del tutor, todos los cursos, todas las secciones y todos los tutores simultáneamente para eficiencia
      const [
        myCoursesResult,
        allCoursesResult,
        allSectionsResult,
        allTutorsResult
      ] = await Promise.all([
        getTutorCourses(user.uid),
        getAllCourses(),
        getAllSections(),
        getAllTutorsBasic()
      ])

      if (myCoursesResult.success) {
        setMyCourses(myCoursesResult.data)
      }

      if (allCoursesResult.success) {
        setAllCourses(allCoursesResult.data)
      }

      if (allSectionsResult.success) {
        setAllSections(allSectionsResult.data)
        if (!hasSetDefaultWeekends) {
          const hasWeekend = allSectionsResult.data.some(sec => sec.day === 'Sábado' || sec.day === 'Domingo')
          setShowWeekends(hasWeekend)
          setHasSetDefaultWeekends(true)
        }
      }

      if (allTutorsResult.success) {
        setAllTutorsData(allTutorsResult.data)
      }
    } catch (error) {
      console.error('Error loading courses:', error)
    }
    setLoading(false)
  }

  const handleLogout = async () => {
    await logoutUser()
    navigate('/login')
  }

  const handleDeleteCourse = async (e, courseId, courseName) => {
    e.stopPropagation() // Prevenir navegación al curso

    if (!window.confirm(`¿Estás seguro de abandonar/eliminar la clase "${courseName}"? Esta acción no se puede deshacer.`)) {
      return
    }

    try {
      setLoading(true) // Mostrar loading mientras se elimina
      const result = await deleteCourse(courseId, user.uid)
      if (result.success) {
        // Recargar cursos después de borrar
        await loadCourses()
      } else {
        alert('Error al procesar la clase: ' + result.error)
      }
    } catch (error) {
      alert('Error al eliminar la clase')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const filteredAllCourses = allCourses.filter(course => {
    const isAlreadyJoined = myCourses.some(myCourse => myCourse.id === course.id)
    return !isAlreadyJoined && course.name.toLowerCase().includes(searchTerm.toLowerCase())
  })

  // Wall Post Handlers
  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setPostError('Selecciona una imagen válida (JPG, PNG, GIF)')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setPostError('La imagen debe ser menor a 5MB')
      return
    }

    setPostImage(file)
    setPostError('')

    const reader = new FileReader()
    reader.onloadend = () => setPostImagePreview(reader.result)
    reader.readAsDataURL(file)
  }

  const handlePostSubmit = async (e) => {
    e.preventDefault()
    if (!postContent.trim()) {
      setPostError('El contenido de la publicación no puede estar vacío')
      return
    }

    setIsPosting(true)
    setPostError('')
    setPostSuccess('')

    try {
      let imageUrl = null

      // Upload image if selected
      if (postImage) {
        // Imitar EXACTAMENTE la misma estructura que ProfileEditor por si hay restricciones RegExp (empezar con número)
        const storageRef = ref(storage, `profile-photos/${user.uid}/${Date.now()}_wall_${postImage.name}`)
        const snapshot = await uploadBytes(storageRef, postImage)
        imageUrl = await getDownloadURL(snapshot.ref)
      }

      const postData = {
        content: postContent.trim(),
        authorName: capitalizeName(tutorProfile?.fullName) || user.email,
        authorAvatar: tutorProfile?.photoURL || '',
      }

      if (postLinkUrl) {
        postData.linkUrl = postLinkUrl.trim()
        postData.linkTitle = postLinkTitle.trim() || 'Ver Enlace'
      }

      if (imageUrl) {
        postData.imageUrl = imageUrl
      }

      const result = await createWallPost(postData, user.uid)

      if (result.success) {
        setPostSuccess('¡Publicación creada exitosamente!')
        setPostContent('')
        setPostLinkTitle('')
        setPostLinkUrl('')
        setPostImage(null)
        setPostImagePreview(null)
        setTimeout(() => setPostSuccess(''), 4000)
      } else {
        setPostError(result.error)
      }
    } catch (error) {
      setPostError('Error al crear la publicación: ' + error.message)
    } finally {
      setIsPosting(false)
    }
  }

  const handleOpenEditCalendarModal = (section) => {
    setEditingSection(section)
    const timeParts = section.time?.split(' - ') || ['', '']
    const locationParts = section.location?.match(/Edificio (.+?) Aula (.+)/) || []
    const isVirtual = section.location === 'Virtual' || !!section.meetingLink

    setCalendarFormData({
      courseId: section.courseId || '',
      day: section.day || '',
      date: section.date || '',
      startTime: timeParts[0] || '',
      endTime: timeParts[1] || '',
      building: locationParts[1] || '',
      classroom: locationParts[2] || '',
      meetingLink: section.meetingLink || '',
      topic: section.topic || '',
      locationType: isVirtual ? 'virtual' : 'physical'
    })
    setCalendarError('')
    setShowCalendarFormModal(true)
  }

  const handleCalendarFormSubmit = async (e) => {
    e.preventDefault()
    setCalendarError('')

    const { courseId, day, date, startTime, endTime, building, classroom, meetingLink, topic, locationType } = calendarFormData

    const currentCourseId = editingSection ? editingSection.courseId : courseId

    if (!currentCourseId || !day || !startTime || !endTime) {
      setCalendarError('Clase, Día, Hora Inicio y Hora Fin son obligatorios')
      return
    }

    if (locationType === 'physical') {
      if (!building || !classroom) {
        setCalendarError('Edificio y Aula son requeridos para ubicación física')
        return
      }
    } else {
      if (!meetingLink || !topic) {
        setCalendarError('Enlace y Sílabo son requeridos para ubicación virtual')
        return
      }
    }

    // Validar que la hora de fin sea posterior a la hora de inicio
    const startHour = parseInt(startTime.split(':')[0])
    const startMinute = parseInt(startTime.split(':')[1].split(' ')[0])
    const startPeriod = startTime.split(' ')[1]
    const endHour = parseInt(endTime.split(':')[0])
    const endMinute = parseInt(endTime.split(':')[1].split(' ')[0])
    const endPeriod = endTime.split(' ')[1]

    let startTime24 = startHour === 12 ? 0 : startHour
    if (startPeriod === 'PM') startTime24 += 12
    let endTime24 = endHour === 12 ? 0 : endHour
    if (endPeriod === 'PM') endTime24 += 12

    const startMinutes = startTime24 * 60 + startMinute
    const endMinutes = endTime24 * 60 + endMinute

    if (endMinutes <= startMinutes) {
      setCalendarError('La hora de fin debe ser posterior a la hora de inicio')
      return
    }

    const sectionData = {
      day,
      date,
      time: `${startTime} - ${endTime}`
    }

    if (locationType === 'virtual') {
      sectionData.location = 'Virtual'
      sectionData.meetingLink = meetingLink
      sectionData.topic = topic
    } else {
      sectionData.location = `Edificio ${building} Aula ${classroom}`
      // Limpiar campos virtuales para evitar remanentes en Firestore
      sectionData.meetingLink = null
      sectionData.topic = null
    }

    try {
      if (editingSection) {
        const result = await updateSection(editingSection.id, sectionData)
        if (result.success) {
          setShowCalendarFormModal(false)
          loadCourses()
        } else {
          setCalendarError(result.error || 'Error al actualizar la sección')
        }
      } else {
        const result = await createSection(sectionData, currentCourseId, user.uid)
        if (result.success) {
          setShowCalendarFormModal(false)
          loadCourses()
        } else {
          setCalendarError(result.error || 'Error al crear la sección')
        }
      }
    } catch (err) {
      setCalendarError(err.message)
    }
  }

  const handleDeleteCalendarSection = async (sectionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sección?')) {
      return
    }
    try {
      const result = await deleteSection(sectionId)
      if (result.success) {
        loadCourses()
      } else {
        alert('Error al eliminar la sección: ' + result.error)
      }
    } catch {
      alert('Error al eliminar sección')
    }
  }

  const handleOpenModerationModal = (userToMod) => {
    setModeratingUser(userToMod)
    const userRoles = userToMod.tutorRoles && Array.isArray(userToMod.tutorRoles)
      ? userToMod.tutorRoles
      : (userToMod.tutorType === 'eucs' ? ['eucs'] : (userToMod.tutorType === 'aurora' ? ['aurora'] : (userToMod.tutorType === 'phuma' ? ['phuma'] : ['vtic'])))
    setModerationFormData({
      tutorRoles: userRoles,
      isAdmin: userToMod.isAdmin === true,
      isAccredited: userToMod.isAccredited === true,
      isRetired: userToMod.isRetired === true,
      retiredData: {
        startDate: userToMod.retiredData?.startDate || '',
        endDate: userToMod.retiredData?.endDate || '',
        honorBio: userToMod.retiredData?.honorBio || ''
      }
    })
    setModerationError('')
    setShowModerationModal(true)
  }

  const handleModerationRoleToggle = (role) => {
    setModerationFormData(prev => {
      const currentRoles = prev.tutorRoles || []
      const updatedRoles = currentRoles.includes(role)
        ? currentRoles.filter(r => r !== role)
        : [...currentRoles, role]
      return {
        ...prev,
        tutorRoles: updatedRoles
      }
    })
  }

  const handleModerationSubmit = async (e) => {
    e.preventDefault()
    if (!moderatingUser) return

    if (moderationFormData.tutorRoles.length === 0) {
      setModerationError('Por favor selecciona al menos un rol de tutor')
      return
    }

    if (moderationFormData.isRetired) {
      if (!moderationFormData.retiredData.startDate || !moderationFormData.retiredData.endDate) {
        setModerationError('Por favor especifica la fecha de inicio y fin del voluntariado para el retiro')
        return
      }
    }

    setModerationSaving(true)
    setModerationError('')

    try {
      const result = await updateUserRolesAndAdminStatus(
        moderatingUser.id,
        moderationFormData.tutorRoles,
        moderationFormData.isAdmin,
        moderationFormData.isAccredited,
        moderationFormData.isRetired,
        moderationFormData.isRetired ? moderationFormData.retiredData : null
      )

      if (result.success) {
        setShowModerationModal(false)
        await loadCourses()
      } else {
        setModerationError(result.error || 'Error al actualizar usuario')
      }
    } catch (err) {
      setModerationError(err.message)
    } finally {
      setModerationSaving(false)
    }
  }

  const profileUrl = tutorProfile?.slug
    ? `${window.location.origin}/tutor/${tutorProfile.slug}`
    : `${window.location.origin}/tutor/${user?.uid}`

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div>Cargando...</div>
      </div>
    )
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h1>Panel de Tutor</h1>
          <p>Bienvenido, {capitalizeName(tutorProfile?.fullName) || user?.email}</p>
        </div>
        <div className="dashboard-actions">
          <button
            className="home-button"
            onClick={() => navigate('/')}
          >
            <Home size={20} />
            Inicio
          </button>
          <button
            className="logout-button"
            onClick={handleLogout}
          >
            <LogOut size={20} />
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Navegación Principal (Categorías) */}
      <div className="dashboard-categories">
        {(isNormalTutor || isAdmin) && (
          <button 
            className={`category-tab ${activeCategory === 'tutorias' ? 'active' : ''}`} 
            onClick={() => handleCategoryChange('tutorias')}
          >
            <GraduationCap size={18} style={{ marginRight: '6px' }} />
            Tutorías
          </button>
        )}
        {(isNormalTutor || isAdmin) && (
          <button 
            className={`category-tab ${activeCategory === 'investigacion' ? 'active' : ''}`} 
            onClick={() => handleCategoryChange('investigacion')}
          >
            <Microscope size={18} style={{ marginRight: '6px' }} />
            Investigación
          </button>
        )}
        <button 
          className={`category-tab ${activeCategory === 'usuario' ? 'active' : ''}`} 
          onClick={() => handleCategoryChange('usuario')}
        >
          <User size={18} style={{ marginRight: '6px' }} />
          Usuario
        </button>
        {isAdmin && (
          <button 
            className={`category-tab ${activeCategory === 'administracion' ? 'active' : ''}`} 
            onClick={() => handleCategoryChange('administracion')}
          >
            <Settings size={18} style={{ marginRight: '6px' }} />
            Administración
          </button>
        )}
      </div>

      {/* Navegación Secundaria (Pestañas) */}
      <div className="dashboard-tabs secondary-tabs">
        {activeCategory === 'tutorias' && (
          <>
            {(isNormalTutor || isAdmin) && (
              <>
                <button
                  className={`dashboard-tab ${activeTab === 'weekly' ? 'active' : ''}`}
                  onClick={() => handleTabChange('weekly')}
                >
                  <Calendar size={18} />
                  Mi Horario
                </button>
                <button
                  className={`dashboard-tab ${activeTab === 'dist' ? 'active' : ''}`}
                  onClick={() => handleTabChange('dist')}
                >
                  <BarChart3 size={18} />
                  Estadísticas
                </button>
                <button
                  className={`dashboard-tab ${activeTab === 'classes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('classes')}
                >
                  <BookOpen size={18} />
                  Mis Clases
                </button>
              </>
            )}
            {hasCalendarRole && (
              <button
                className={`dashboard-tab ${activeTab === 'calendar' ? 'active' : ''}`}
                onClick={() => handleTabChange('calendar')}
              >
                <Calendar size={18} />
                Calendario
              </button>
            )}
          </>
        )}

        {activeCategory === 'investigacion' && (
          <>
            <button
              className={`dashboard-tab ${activeTab === 'research_groups' ? 'active' : ''}`}
              onClick={() => handleTabChange('research_groups')}
            >
              <Microscope size={18} />
              Administrar Grupos
            </button>
          </>
        )}

        {activeCategory === 'usuario' && (
          <>
            <button
              className={`dashboard-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => handleTabChange('profile')}
            >
              <User size={18} />
              Mi Perfil
            </button>
            <button
              className={`dashboard-tab ${activeTab === 'carnet' ? 'active' : ''}`}
              onClick={() => handleTabChange('carnet')}
            >
              <IdCard size={18} />
              Carnet Digital
            </button>
            <button
              className={`dashboard-tab ${activeTab === 'wall' ? 'active' : ''}`}
              onClick={() => handleTabChange('wall')}
            >
              <MessageSquare size={18} />
              Publicar
            </button>
            <button
              className={`dashboard-tab ${activeTab === 'qr' ? 'active' : ''}`}
              onClick={() => handleTabChange('qr')}
            >
              <QrCode size={18} />
              Mi Código QR
            </button>
          </>
        )}

        {activeCategory === 'administracion' && (
          <>
            <button
              className={`dashboard-tab ${activeTab === 'admin_users' ? 'active' : ''}`}
              onClick={() => handleTabChange('admin_users')}
            >
              <Users size={18} />
              Administrar Usuarios
            </button>
          </>
        )}
      </div>

      <div className="dashboard-content">
        {/* Tab: Grupos de Investigacion */}
        {activeTab === 'research_groups' && (
          <div className="dashboard-section" style={{ background: 'transparent', boxShadow: 'none', padding: 0 }}>
            <ResearchGroupsDashboard user={user} />
          </div>
        )}
        {/* Tab: Mi Horario Semanal */}
        {activeTab === 'weekly' && (
          <>
            {myCourses.length > 0 && totalSections > 0 ? (
              <div className="dashboard-section weekly-calendar-section">
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2>
                    <Calendar size={24} color="var(--primary-blue)" />
                    Mi Horario Semanal
                  </h2>
                  <button
                    className="toggle-weekend-btn"
                    onClick={() => setShowWeekends(!showWeekends)}
                  >
                    {showWeekends ? 'Ocultar Fin de Semana' : 'Mostrar Fin de Semana'}
                  </button>
                </div>
                {(() => {
                  const daysToRender = showWeekends 
                    ? DAYS_OF_WEEK 
                    : DAYS_OF_WEEK.filter(day => day !== 'Sábado' && day !== 'Domingo')
                  return (
                    <div className="weekly-calendar-grid" style={{ gridTemplateColumns: `repeat(${daysToRender.length}, minmax(180px, 1fr))` }}>
                      {daysToRender.map(day => {
                        const sectionsForDay = myCourses.flatMap(course =>
                          (course.sections || [])
                            .filter(sec => sec.day === day)
                            .map(sec => ({ ...sec, courseName: course.name, courseId: course.id }))
                        )
                        sectionsForDay.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                        const isToday = day === getCurrentDayName()

                        return (
                          <div key={day} className={`calendar-day-column ${isToday ? 'is-today' : ''}`}>
                            <div className="calendar-day-header">{day}</div>
                            <div className="calendar-day-content">
                              {sectionsForDay.length > 0 ? (
                                sectionsForDay.map((sec, idx) => {
                                  const colorStyle = getCourseColor(sec.courseId)
                                  return (
                                    <div 
                                      key={idx} 
                                      className="calendar-section-card" 
                                      style={{
                                        backgroundColor: colorStyle.bg,
                                        borderColor: colorStyle.border,
                                        borderLeft: `5px solid ${colorStyle.border}`,
                                        color: colorStyle.text
                                      }}
                                    >
                                      <div className="sec-course-name">{sec.courseName}</div>
                                      <div className="sec-time">
                                        <Clock size={12} /> {sec.time}
                                        {sec.date && <span style={{ marginLeft: '4px', fontSize: '10px', fontWeight: 'bold' }}>({formatDateShort(sec.date)})</span>}
                                      </div>
                                      <div className="sec-location">
                                        <MapPin size={12} /> {sec.location || 'Por definir'}
                                      </div>
                                      {sec.meetingLink && (
                                        <div className="sec-virtual-info" style={{ marginTop: '8px' }}>
                                          <a 
                                            href={sec.meetingLink} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            onClick={(e) => e.stopPropagation()}
                                            className="meeting-link-badge"
                                            style={{
                                              backgroundColor: colorStyle.text,
                                              color: 'white'
                                            }}
                                          >
                                            <LinkIcon size={10} />
                                            <span>Unirse</span>
                                          </a>
                                          {sec.topic && (
                                            <div className="sec-topic" title={sec.topic} style={{ color: colorStyle.text }}>
                                              {sec.topic}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })
                              ) : (
                                <div className="calendar-empty-day">Sin clases</div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            ) : (
              <div className="dashboard-section">
                <div className="empty-state">
                  <Calendar size={48} />
                  <p>Aún no tienes horarios asignados para esta semana.</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tab: Calendario General (para Aurora y Phuma, editable por VTIC) */}
        {activeTab === 'calendar' && (
          <div className="dashboard-section weekly-calendar-section">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <h2>
                <Calendar size={24} color="var(--primary-blue)" />
                Calendario General de Secciones
              </h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  className="toggle-weekend-btn"
                  onClick={() => setShowWeekends(!showWeekends)}
                >
                  {showWeekends ? 'Ocultar Fin de Semana' : 'Mostrar Fin de Semana'}
                </button>
                {canEditCalendar && (
                  <button
                    className="add-section-button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: 'var(--primary-blue)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                    onClick={() => {
                      setEditingSection(null)
                      setCalendarFormData({
                        courseId: '',
                        day: '',
                        date: '',
                        startTime: '',
                        endTime: '',
                        building: '',
                        classroom: '',
                        meetingLink: '',
                        topic: '',
                        locationType: 'physical'
                      })
                      setCalendarError('')
                      setShowCalendarFormModal(true)
                    }}
                  >
                    <Plus size={18} />
                    Crear Horario
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const daysToRender = showWeekends 
                ? DAYS_OF_WEEK 
                : DAYS_OF_WEEK.filter(day => day !== 'Sábado' && day !== 'Domingo')
              return (
                <div className="weekly-calendar-grid" style={{ gridTemplateColumns: `repeat(${daysToRender.length}, minmax(180px, 1fr))` }}>
                  {daysToRender.map(day => {
                    const daySections = allSections.filter(sec => sec.day === day)
                    
                    const formattedSections = daySections.reduce((acc, sec) => {
                      const course = allCourses.find(c => c.id === sec.courseId)
                      if (!course) return acc
                      const tutor = allTutorsData.find(t => t.id === sec.createdBy)
                      acc.push({
                        ...sec,
                        courseName: course.name,
                        tutorName: tutor ? (tutor.fullName || tutor.email) : 'Tutor Desconocido'
                      })
                      return acc
                    }, [])

                    formattedSections.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                    const isToday = day === getCurrentDayName()

                    return (
                      <div key={`cal-${day}`} className={`calendar-day-column ${isToday ? 'is-today' : ''}`}>
                        <div className="calendar-day-header">{day}</div>
                        <div className="calendar-day-content">
                          {formattedSections.length > 0 ? (
                            formattedSections.map((sec, idx) => {
                              const colorStyle = getCourseColor(sec.courseId)
                              return (
                                <div 
                                  key={`cal-sec-${idx}`} 
                                  className="calendar-section-card" 
                                  style={{ 
                                    position: 'relative', 
                                    paddingRight: canEditCalendar ? '35px' : '12px',
                                    backgroundColor: colorStyle.bg,
                                    borderColor: colorStyle.border,
                                    borderLeft: `5px solid ${colorStyle.border}`,
                                    color: colorStyle.text
                                  }}
                                >
                                  <div className="sec-course-name">{sec.courseName}</div>
                                  <div className="sec-tutor-name" title={sec.tutorName}>
                                    <User size={12} /> {sec.tutorName}
                                  </div>
                                  <div className="sec-time">
                                    <Clock size={12} /> {sec.time}
                                    {sec.date && <span style={{ marginLeft: '4px', fontSize: '10px', fontWeight: 'bold' }}>({formatDateShort(sec.date)})</span>}
                                  </div>
                                  <div className="sec-location">
                                    <MapPin size={12} /> {sec.location || 'Por definir'}
                                  </div>
                                  {sec.meetingLink && (
                                    <div className="sec-virtual-info" style={{ marginTop: '8px' }}>
                                      <a 
                                        href={sec.meetingLink} 
                                        target="_blank" 
                                        rel="noreferrer" 
                                        onClick={(e) => e.stopPropagation()}
                                        className="meeting-link-badge"
                                        style={{
                                          backgroundColor: colorStyle.text,
                                          color: 'white'
                                        }}
                                      >
                                        <LinkIcon size={10} />
                                        <span>Unirse</span>
                                      </a>
                                      {sec.topic && (
                                        <div className="sec-topic" title={sec.topic} style={{ color: colorStyle.text }}>
                                          {sec.topic}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {canEditCalendar && (
                                    <div className="section-card-actions">
                                      <button
                                        className="card-action-btn edit-btn"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleOpenEditCalendarModal(sec)
                                        }}
                                        title="Editar"
                                      >
                                        <Edit size={12} />
                                      </button>
                                      <button
                                        className="card-action-btn delete-btn"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleDeleteCalendarSection(sec.id)
                                        }}
                                        title="Eliminar"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )
                            })
                          ) : (
                            <div className="calendar-empty-day">Sin clases</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}

        {/* Tab: Administración de Usuarios */}
        {activeTab === 'admin_users' && isAdmin && (
          <div className="dashboard-section admin-section">
            <div className="section-header">
              <h2>
                <Users size={24} color="var(--primary-blue)" />
                Gestión y Moderación de Usuarios
              </h2>
              <span className="badge">{allTutorsData.length} usuarios</span>
            </div>

            {/* Visualización de Llaves de Registro */}
            <div className="admin-keys-card" style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              marginTop: '16px'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Key size={16} color="var(--primary-blue)" />
                Llaves de Registro Activas por Rol (Referencia de Administración)
              </h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: '#64748b' }}>
                Comparte estas llaves con los nuevos tutores para que al registrarse obtengan automáticamente el rol correspondiente sin poder seleccionarlo libremente.
              </p>
              <div className="keys-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {Object.entries(ROLE_KEYS).map(([role, keyVal]) => (
                  <div key={role} style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#64748b' }}>
                      {ROLE_LABELS[role] || role}
                    </span>
                    <code style={{
                      fontSize: '0.9rem',
                      fontFamily: 'monospace',
                      color: 'var(--primary-blue)',
                      background: '#f1f5f9',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      width: 'fit-content'
                    }}>
                      {keyVal}
                    </code>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="admin-table-container" style={{ overflowX: 'auto', marginTop: '20px' }}>
              <table className="admin-users-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#4a5568', fontWeight: 'bold' }}>
                     <th style={{ padding: '12px' }}>Usuario</th>
                     <th style={{ padding: '12px' }}>Contacto / Ubicación</th>
                     <th style={{ padding: '12px' }}>Carrera / Año</th>
                     <th style={{ padding: '12px' }}>Fechas Registro/Acceso</th>
                     <th style={{ padding: '12px' }}>Roles de Tutor</th>
                     <th style={{ padding: '12px' }}>Admin</th>
                     <th style={{ padding: '12px', textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {allTutorsData.map(u => {
                    const uRoles = u.tutorRoles && Array.isArray(u.tutorRoles)
                      ? u.tutorRoles
                      : (u.tutorType === 'eucs' ? ['eucs'] : (u.tutorType === 'aurora' ? ['aurora'] : (u.tutorType === 'phuma' ? ['phuma'] : ['vtic'])))

                    const formatTimestamp = (ts) => {
                      if (!ts) return 'No disponible'
                      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString('es-ES')
                      return new Date(ts).toLocaleDateString('es-ES')
                    }

                    return (
                      <tr key={u.id} style={{ borderBottom: '1px solid #edf2f7', transition: 'background-color 0.2s', backgroundColor: u.isDisabled ? '#fff5f5' : 'transparent' }} className="admin-table-row-hover">
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ fontWeight: 'bold', color: u.isDisabled ? '#e53e3e' : '#2d3748', textDecoration: u.isDisabled ? 'line-through' : 'none' }}>
                              {u.fullName || 'Sin Nombre'}
                            </div>
                            {u.isDisabled && (
                              <span style={{
                                backgroundColor: '#fee2e2',
                                color: '#e53e3e',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '9px',
                                fontWeight: 'bold'
                              }}>
                                DESHABILITADO
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#718096' }}>{u.email}</div>
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9rem', color: '#4a5568' }}>
                          <div>{u.phone || 'Sin teléfono'}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>{u.location || 'Sin ubicación'}</div>
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9rem', color: '#4a5568' }}>
                          <div>{u.career || 'Sin carrera'}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>{u.year || '-'}</div>
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.85rem', color: '#4a5568' }}>
                          <div><strong>Creado:</strong> {formatTimestamp(u.createdAt)}</div>
                          <div><strong>Act.:</strong> {formatTimestamp(u.updatedAt)}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {uRoles.map(role => {
                              const rConfig = {
                                vtic: { label: 'Lucem Aspicio', bg: '#11163e', color: '#ffffff' },
                                aurora: { label: 'Aurora', bg: '#D10056', color: '#ffffff' },
                                phuma: { label: 'Phuma', bg: '#B35C00', color: '#ffffff' },
                                eucs: { label: 'EUCS', bg: '#7E161C', color: '#ffffff' }
                              }[role] || { label: role, bg: '#6b7280', color: '#ffffff' }
                              return (
                                <span key={role} style={{
                                  backgroundColor: rConfig.bg,
                                  color: rConfig.color,
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>
                                  {rConfig.label}
                                </span>
                              )
                            })}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            backgroundColor: u.isAdmin ? '#d1fae5' : '#fee2e2',
                            color: u.isAdmin ? '#065f46' : '#991b1b',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            {u.isAdmin ? 'Sí' : 'No'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <button
                            style={{
                              padding: '6px 12px',
                              backgroundColor: 'var(--primary-blue)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              transition: 'opacity 0.2s'
                            }}
                            onClick={() => handleOpenModerationModal(u)}
                            className="admin-edit-btn"
                          >
                            Moderar Roles
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab: Mi Perfil */}
        {activeTab === 'profile' && (
          <div className="dashboard-section">
            <ProfileEditor isEmbedded={true} onDirtyChange={setIsProfileDirty} />
          </div>
        )}

        {/* Tab: Publicar en Muro */}
        {activeTab === 'wall' && (
          <div className="dashboard-section wall-composer-section">
            <div className="section-header">
              <h2>
                <MessageSquare size={24} color="var(--primary-blue)" />
                Nueva Publicación en el Muro
              </h2>
            </div>
            <div className="wall-composer-card">
              <p className="composer-hint">Comparte conocimiento, libros, investigaciones, o material útil con toda la comunidad estudiantil. Todos podrán ver tu aporte en la pestaña pública "Muro".</p>

              {postError && <div className="error-message" style={{ marginBottom: '20px' }}>{postError}</div>}
              {postSuccess && <div className="success-message" style={{ marginBottom: '20px' }}>{postSuccess}</div>}

              <form onSubmit={handlePostSubmit} className="wall-form">
                <div className="form-group">
                  <label htmlFor="postContent">Contenido de la Publicación *</label>
                  <textarea
                    id="postContent"
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    placeholder="Escribe aquí tu aporte..."
                    rows={6}
                    disabled={isPosting}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="postLinkTitle">
                      <LinkIcon size={16} style={{ marginRight: '6px' }} />
                      Título del Enlace (Opcional)
                    </label>
                    <input
                      type="text"
                      id="postLinkTitle"
                      value={postLinkTitle}
                      onChange={(e) => setPostLinkTitle(e.target.value)}
                      placeholder="Ej: Libro de Cálculo, Drive PDF..."
                      disabled={isPosting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="postLinkUrl">
                      URL del Enlace (Opcional)
                    </label>
                    <input
                      type="url"
                      id="postLinkUrl"
                      value={postLinkUrl}
                      onChange={(e) => setPostLinkUrl(e.target.value)}
                      placeholder="https://..."
                      disabled={isPosting}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <ImageIcon size={16} style={{ marginRight: '6px' }} />
                    Imagen Adjunta (Opcional)
                  </label>

                  {postImagePreview ? (
                    <div className="image-preview-container">
                      <img src={postImagePreview} alt="Preview" className="composer-image-preview" />
                      <button
                        type="button"
                        onClick={() => {
                          setPostImage(null)
                          setPostImagePreview(null)
                        }}
                        className="remove-image-btn"
                        disabled={isPosting}
                      >
                        <Trash2 size={16} /> Quitar imagen
                      </button>
                    </div>
                  ) : (
                    <div className="image-upload-area" onClick={() => fileInputRef.current?.click()}>
                      <Upload size={32} color="#a0aec0" />
                      <span>Haz clic para subir una imagen ilustrativa</span>
                      <small>Solo archivos PNG, JPG, GIF menores a 5MB</small>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={isPosting}
                  />
                </div>

                <div className="form-actions" style={{ border: 'none', paddingTop: '16px' }}>
                  <button type="submit" className="save-button" disabled={isPosting || !postContent.trim()}>
                    {isPosting ? 'Publicando...' : 'Publicar en el Muro'}
                    <MessageSquare size={18} style={{ marginLeft: '8px' }} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tab: Mi Código QR */}
        {activeTab === 'qr' && (
          <div className="dashboard-section qr-dashboard-section">
            <div className="section-header">
              <h2>
                <QrCode size={24} color="var(--primary-blue)" />
                Mi Código QR y Enlace
              </h2>
            </div>
            <div className="qr-dashboard-card">
              <div className="qr-display">
                <QRCodeCanvas
                  value={profileUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                  className="dashboard-qr"
                />
              </div>
              <div className="qr-info">
                <h3>¡Comparte tu perfil!</h3>
                <p>Usa este código QR o el siguiente enlace para que otros estudiantes lleguen directamente a tu perfil de tutor.</p>

                <div className="share-link-box">
                  <input type="text" value={profileUrl} readOnly />
                  <button onClick={() => {
                    navigator.clipboard.writeText(profileUrl)
                    alert('¡Enlace copiado al portapapeles!')
                  }}>
                    Copiar
                  </button>
                </div>

                {!tutorProfile?.slug && (
                  <div className="slug-warning">
                    <p>💡 <strong>Tip:</strong> Puedes crear una URL personalizada (como <code>/tutor/tu-nombre</code>) en la pestaña de <strong>Mi Perfil</strong>.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab: Carnet Digital */}
        {activeTab === 'carnet' && (
          <div className="dashboard-section carnet-dashboard-section">
            <div className="section-header">
              <h2>
                <IdCard size={24} color="var(--primary-blue)" />
                Carnet Digital
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem', marginTop: '4px' }}>
                Este es tu carnet digital oficial de VTIC. Haz clic sobre él para expandirlo a pantalla completa (ideal para móviles).
              </p>
            </div>

            {(!tutorProfile?.dni || !tutorProfile?.accountNumber) && (
              <div className="carnet-warning-banner">
                💡 <strong>Completar Perfil:</strong> Para que tu carnet muestre todos tus datos oficiales (DNI e Identidad), por favor ve a la pestaña de <strong>Mi Perfil</strong> y llénalos.
              </div>
            )}

            <div className="carnet-view-container">
              <div className="carnet-card-wrapper" onClick={() => setShowFullscreenCarnet(true)}>
                <div className="carnet-card">
                  <div className="carnet-card-border">
                    <div className="carnet-card-content">
                      
                      {/* Top Header Section */}
                      <div className="carnet-card-header">
                        {/* Logos Left */}
                        <div className="carnet-logos-left">
                          <img 
                            src="https://res.cloudinary.com/dcm2dsjov/image/upload/v1757627650/Agregar_un_subt%C3%ADtulo_10_irdlej.png" 
                            alt="VTIC Logo" 
                            className="carnet-main-logo"
                          />
                          <div className="carnet-sublogos">
                            <img src="/carnet/voluntariado_logo.png" alt="Voluntariado Universitario" className="carnet-sublogo-item" />
                            <img src="/carnet/voae_logo.png" alt="VOAE" className="carnet-sublogo-item" />
                            <img src="/carnet/unah_cortes_logo.png" alt="UNAH Campus Cortés" className="carnet-sublogo-item" />
                          </div>
                        </div>

                        {/* Photo/Initials Right */}
                        <div className="carnet-photo-right">
                          <div className="carnet-initials">{getInitials(tutorProfile?.fullName || user?.email)}</div>
                          <div className="carnet-photo-container">
                            {tutorProfile?.photoURL ? (
                              <img src={tutorProfile.photoURL} alt="Foto Perfil" />
                            ) : (
                              <div className="carnet-photo-placeholder">
                                <User size={40} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Middle Body Section */}
                      <div className="carnet-card-body">
                        <div className="carnet-year-career">
                          <div className="carnet-year">{tutorProfile?.year || new Date().getFullYear().toString()}</div>
                          <div className="carnet-career">{tutorProfile?.career || 'Carrera no especificada'}</div>
                        </div>
                        <div className="carnet-qr-container">
                          <QRCodeCanvas
                            value={profileUrl}
                            size={65}
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                      </div>

                      {/* Horizontal Thick Navy Divider */}
                      <div className="carnet-card-divider"></div>

                      {/* Bottom Footer Section */}
                      <div className="carnet-card-footer-container">
                        <div className="carnet-card-footer">
                          <div className="carnet-name">{capitalizeName(tutorProfile?.fullName) || user?.email}</div>
                          <div className="carnet-numbers">
                            <span className="carnet-dni">{tutorProfile?.dni ? tutorProfile.dni.replace(/(\d{4})(\d{4})(\d{5})/, '$1-$2-$3') : 'DNI No asignado'}</span>
                            <span className="carnet-account">{tutorProfile?.accountNumber || 'Cuenta No asignada'}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fullscreen modal */}
            {showFullscreenCarnet && (
              <div className="carnet-modal-overlay" onClick={() => setShowFullscreenCarnet(false)}>
                <div className="carnet-modal-content" onClick={(e) => e.stopPropagation()}>
                  <button className="carnet-modal-close" onClick={() => setShowFullscreenCarnet(false)}>
                    &times;
                  </button>

                  <div className="carnet-card is-fullscreen">
                    <div className="carnet-card-border">
                      <div className="carnet-card-content">
                        
                        {/* Top Header Section */}
                        <div className="carnet-card-header">
                          <div className="carnet-logos-left">
                            <img 
                              src="https://res.cloudinary.com/dcm2dsjov/image/upload/v1757627650/Agregar_un_subt%C3%ADtulo_10_irdlej.png" 
                              alt="VTIC Logo" 
                              className="carnet-main-logo"
                            />
                            <div className="carnet-sublogos">
                              <img src="/carnet/voluntariado_logo.png" alt="Voluntariado Universitario" className="carnet-sublogo-item" />
                              <img src="/carnet/voae_logo.png" alt="VOAE" className="carnet-sublogo-item" />
                              <img src="/carnet/unah_cortes_logo.png" alt="UNAH Campus Cortés" className="carnet-sublogo-item" />
                            </div>
                          </div>

                          <div className="carnet-photo-right">
                            <div className="carnet-initials">{getInitials(tutorProfile?.fullName || user?.email)}</div>
                            <div className="carnet-photo-container">
                              {tutorProfile?.photoURL ? (
                                <img src={tutorProfile.photoURL} alt="Foto Perfil" />
                              ) : (
                                <div className="carnet-photo-placeholder">
                                  <User size={80} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Middle Body Section */}
                        <div className="carnet-card-body">
                          <div className="carnet-year-career">
                            <div className="carnet-year">{tutorProfile?.year || new Date().getFullYear().toString()}</div>
                            <div className="carnet-career">{tutorProfile?.career || 'Carrera no especificada'}</div>
                          </div>
                          <div className="carnet-qr-container">
                            <QRCodeCanvas
                              value={profileUrl}
                              size={80}
                              level="M"
                              includeMargin={false}
                            />
                          </div>
                        </div>

                        {/* Horizontal Thick Navy Divider */}
                        <div className="carnet-card-divider"></div>

                        {/* Bottom Footer Section */}
                        <div className="carnet-card-footer-container">
                          <div className="carnet-card-footer">
                            <div className="carnet-name">{capitalizeName(tutorProfile?.fullName) || user?.email}</div>
                            <div className="carnet-numbers">
                              <span className="carnet-dni">{tutorProfile?.dni ? tutorProfile.dni.replace(/(\d{4})(\d{4})(\d{5})/, '$1-$2-$3') : 'DNI No asignado'}</span>
                              <span className="carnet-account">{tutorProfile?.accountNumber || 'Cuenta No asignada'}</span>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>

                  <p className="carnet-modal-hint">Toca en cualquier parte fuera del carnet para cerrar</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Distribución y Estadísticas */}
        {activeTab === 'dist' && (
          <>
            <div className="stats-section-dashboard">
              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'rgba(26, 43, 91, 0.1)' }}>
                  <BookOpen size={24} color="var(--primary-blue)" />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{myCourses.length}</div>
                  <div className="stat-label">Mis Clases</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'rgba(253, 203, 47, 0.1)' }}>
                  <Clock size={24} color="var(--accent-yellow)" />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{totalSections}</div>
                  <div className="stat-label">Secciones Activas</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'rgba(255, 107, 53, 0.1)' }}>
                  <Users size={24} color="var(--accent-orange)" />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{totalTutors}</div>
                  <div className="stat-label">Tutores Colaborando</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'rgba(26, 43, 91, 0.1)' }}>
                  <TrendingUp size={24} color="var(--primary-blue)" />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{Object.keys(sectionsByDay).length}</div>
                  <div className="stat-label">Días con Horarios</div>
                </div>
              </div>
            </div>

            {Object.keys(sectionsByDay).length > 0 && (
              <div className="dashboard-section">
                <div className="section-header">
                  <h2>
                    <BarChart3 size={24} />
                    Distribución de Horarios por Día
                  </h2>
                </div>
                <div className="chart-container">
                  <div className="bar-chart">
                    {Object.entries(sectionsByDay).map(([day, count]) => {
                      const maxCount = Math.max(...Object.values(sectionsByDay), 1)
                      const percentage = (count / maxCount) * 100
                      return (
                        <div key={day} className="bar-chart-item">
                          <div className="bar-label">{day}</div>
                          <div className="bar-wrapper">
                            <div
                              className="bar-fill"
                              style={{
                                width: `${percentage}%`,
                                background: `linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-light) 100%)`
                              }}
                            >
                              <span className="bar-value">{count}</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Nueva Estadística: Distribución de Horarios por Tutor */}
            {(() => {
              const eucsTutors = allTutorsData.filter(t => t.tutorType === 'eucs')
              const eucsTutorIds = new Set(eucsTutors.map(t => t.id))

              // Filtrar secciones válidas que tienen curso (ignorar desconocidos) y son de EUCS
              const validSections = allSections.filter(sec => 
                allCourses.some(c => c.id === sec.courseId) && eucsTutorIds.has(sec.createdBy)
              )
              
              const sectionsByTutor = validSections.reduce((acc, sec) => {
                const tutor = allTutorsData.find(t => t.id === sec.createdBy)
                const tutorName = tutor ? (tutor.fullName || tutor.email) : 'Tutor Desconocido'
                acc[tutorName] = (acc[tutorName] || 0) + 1
                return acc
              }, {})

              // Ordenar tutores por cantidad de clases (mayor a menor)
              const sortedTutors = Object.entries(sectionsByTutor)
                .sort((a, b) => b[1] - a[1])

              if (sortedTutors.length === 0) return null

              const maxTutorCount = Math.max(...sortedTutors.map(([, count]) => count), 1)

              return (
                <div className="dashboard-section">
                  <div className="section-header">
                    <h2>
                      <Users size={24} />
                      Clases por Tutor a la Semana
                    </h2>
                  </div>
                  <div className="chart-container">
                    <div className="bar-chart">
                      {sortedTutors.map(([tutorName, count]) => {
                        const percentage = (count / maxTutorCount) * 100
                        return (
                          <div key={tutorName} className="bar-chart-item">
                            <div className="bar-label">{tutorName}</div>
                            <div className="bar-wrapper">
                              <div
                                className="bar-fill"
                                style={{
                                  width: `${percentage}%`,
                                  background: `linear-gradient(135deg, var(--accent-orange) 0%, #ff8c69 100%)`
                                }}
                              >
                                <span className="bar-value">{count}</span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* General Weekly Calendar for all tutors */}
            <div className="dashboard-section weekly-calendar-section">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>
                  <Calendar size={24} color="var(--primary-blue)" />
                  Calendario General de Secciones
                </h2>
                <button
                  className="toggle-weekend-btn"
                  onClick={() => setShowWeekends(!showWeekends)}
                >
                  {showWeekends ? 'Ocultar Fin de Semana' : 'Mostrar Fin de Semana'}
                </button>
              </div>
              {(() => {
                const daysToRender = showWeekends 
                  ? DAYS_OF_WEEK 
                  : DAYS_OF_WEEK.filter(day => day !== 'Sábado' && day !== 'Domingo')
                return (
                  <div className="weekly-calendar-grid" style={{ gridTemplateColumns: `repeat(${daysToRender.length}, minmax(180px, 1fr))` }}>
                    {daysToRender.map(day => {
                      const eucsTutors = allTutorsData.filter(t => t.tutorType === 'eucs')
                      const eucsTutorIds = new Set(eucsTutors.map(t => t.id))

                      const sectionsForDay = allSections
                        .filter(sec => sec.day === day && eucsTutorIds.has(sec.createdBy))
                        .reduce((acc, sec) => {
                          const course = allCourses.find(c => c.id === sec.courseId)
                          if (!course) return acc // Ignorar secciones de cursos desconocidos

                          const tutor = allTutorsData.find(t => t.id === sec.createdBy)
                          acc.push({
                            ...sec,
                            courseName: course.name,
                            tutorName: tutor ? (tutor.fullName || tutor.email) : 'Tutor Desconocido'
                          })
                          return acc
                        }, [])
                      
                      sectionsForDay.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                      const isToday = day === getCurrentDayName()

                      return (
                        <div key={`gen-${day}`} className={`calendar-day-column ${isToday ? 'is-today' : ''}`}>
                          <div className="calendar-day-header">{day}</div>
                          <div className="calendar-day-content">
                            {sectionsForDay.length > 0 ? (
                              sectionsForDay.map((sec, idx) => {
                                const colorStyle = getCourseColor(sec.courseId)
                                return (
                                  <div 
                                    key={`gen-sec-${idx}`} 
                                    className="calendar-section-card"
                                    style={{
                                      backgroundColor: colorStyle.bg,
                                      borderColor: colorStyle.border,
                                      borderLeft: `5px solid ${colorStyle.border}`,
                                      color: colorStyle.text
                                    }}
                                  >
                                    <div className="sec-course-name">{sec.courseName}</div>
                                    <div className="sec-tutor-name" title={sec.tutorName}>
                                      <User size={12} /> {sec.tutorName}
                                    </div>
                                    <div className="sec-time">
                                      <Clock size={12} /> {sec.time}
                                      {sec.date && <span style={{ marginLeft: '4px', fontSize: '10px', fontWeight: 'bold' }}>({formatDateShort(sec.date)})</span>}
                                    </div>
                                    <div className="sec-location">
                                      <MapPin size={12} /> {sec.location || 'Por definir'}
                                    </div>
                                    {sec.meetingLink && (
                                      <div className="sec-virtual-info" style={{ marginTop: '8px' }}>
                                        <a 
                                          href={sec.meetingLink} 
                                          target="_blank" 
                                          rel="noreferrer" 
                                          onClick={(e) => e.stopPropagation()}
                                          className="meeting-link-badge"
                                          style={{
                                            backgroundColor: colorStyle.text,
                                            color: 'white'
                                          }}
                                        >
                                          <LinkIcon size={10} />
                                          <span>Unirse</span>
                                        </a>
                                        {sec.topic && (
                                          <div className="sec-topic" title={sec.topic} style={{ color: colorStyle.text }}>
                                            {sec.topic}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              })
                            ) : (
                              <div className="calendar-empty-day">Sin clases</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </div>
          </>
        )}

        {/* Tab: Mis Clases */}
        {activeTab === 'classes' && (
          <>
            <div className="quick-actions">
              <button
                className="action-card primary"
                onClick={() => navigate('/dashboard/create-course')}
              >
                <Plus size={32} />
                <h3>Crear Nueva Clase</h3>
                <p>Agrega una nueva materia</p>
              </button>

              <button
                className="action-card"
                onClick={() => setShowJoinModal(true)}
              >
                <Users size={32} />
                <h3>Unirse a Clase</h3>
                <p>Colabora con otros tutores</p>
              </button>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <h2>
                  <BookOpen size={24} />
                  Mis Clases
                </h2>
                <span className="badge">{myCourses.length}</span>
              </div>

              {myCourses.length === 0 ? (
                <div className="empty-state">
                  <BookOpen size={48} />
                  <p>No tienes clases todavía</p>
                  <button
                    className="empty-action"
                    onClick={() => navigate('/dashboard/create-course')}
                  >
                    Crear tu primera clase
                  </button>
                </div>
              ) : (
                <div className="courses-grid">
                  {myCourses.map((course) => (
                    <div
                      key={course.id}
                      className="course-card"
                      onClick={() => navigate(`/dashboard/course/${course.id}`)}
                    >
                      <div className="course-card-header">
                        <h3>{course.name}</h3>
                        <div className="course-status-badge">Activo</div>
                      </div>
                      <p className="course-description">{course.description || 'Sin descripción'}</p>

                      <div className="course-stats-preview">
                        <div className="stat-pill">
                          <Clock size={14} />
                          {course.sections?.length || 0} secciones
                        </div>
                        <div className="stat-pill">
                          <Users size={14} />
                          {course.tutors?.length || 0} tutores
                        </div>
                      </div>

                      <div className="course-card-footer">
                        <button
                          className="delete-course-btn"
                          onClick={(e) => handleDeleteCourse(e, course.id, course.name)}
                          title="Eliminar clase"
                        >
                          <Trash2 size={18} />
                          <span>Eliminar</span>
                        </button>
                        <button className="enter-course-btn">
                          Gestionar <ArrowRight size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal para unirse a clase */}
      {showJoinModal && (
        <div className="modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Unirse a Clase</h2>
              <button
                className="modal-close"
                onClick={() => setShowJoinModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Buscar clase..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="courses-list">
                {filteredAllCourses.length === 0 ? (
                  <p className="no-results">No hay clases disponibles</p>
                ) : (
                  filteredAllCourses.map((course) => (
                    <div
                      key={course.id}
                      className="course-item"
                      onClick={() => {
                        navigate(`/dashboard/join-course/${course.id}`)
                        setShowJoinModal(false)
                      }}
                    >
                      <div>
                        <h4>{course.name}</h4>
                        <p>{course.description || 'Sin descripción'}</p>
                      </div>
                      <ArrowRight size={20} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Crear/Editar Sección desde Calendario */}
      {showCalendarFormModal && (
        <div className="modal-overlay" onClick={() => setShowCalendarFormModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>{editingSection ? 'Editar Sección' : 'Nueva Sección'}</h2>
              <button
                className="modal-close"
                onClick={() => setShowCalendarFormModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {calendarError && (
                <div className="error-message" style={{ marginBottom: '15px' }}>
                  {calendarError}
                </div>
              )}
              <form onSubmit={handleCalendarFormSubmit} className="register-form" style={{ padding: 0, boxShadow: 'none', background: 'none' }}>
                {!editingSection && (
                  <div className="form-group">
                    <label htmlFor="modal-course">Clase *</label>
                    <select
                      id="modal-course"
                      value={calendarFormData.courseId}
                      onChange={(e) => setCalendarFormData({ ...calendarFormData, courseId: e.target.value })}
                      required
                    >
                      <option value="">Selecciona una clase</option>
                      {allCourses.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="form-group">
                  <label htmlFor="modal-day">Día de la Semana *</label>
                  <select
                    id="modal-day"
                    value={calendarFormData.day}
                    onChange={(e) => setCalendarFormData({ ...calendarFormData, day: e.target.value })}
                    required
                  >
                    <option value="">Selecciona un día</option>
                    <option value="Lunes">Lunes</option>
                    <option value="Martes">Martes</option>
                    <option value="Miércoles">Miércoles</option>
                    <option value="Jueves">Jueves</option>
                    <option value="Viernes">Viernes</option>
                    <option value="Sábado">Sábado</option>
                    <option value="Domingo">Domingo</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="modal-date">Fecha (Opcional)</label>
                  <input
                    type="date"
                    id="modal-date"
                    value={calendarFormData.date}
                    onChange={(e) => setCalendarFormData({ ...calendarFormData, date: e.target.value })}
                  />
                </div>

                <div className="form-row" style={{ display: 'flex', gap: '10px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="modal-startTime">Hora Inicio *</label>
                    <select
                      id="modal-startTime"
                      value={calendarFormData.startTime}
                      onChange={(e) => setCalendarFormData({ ...calendarFormData, startTime: e.target.value })}
                      required
                    >
                      <option value="">Inicio</option>
                      {['7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="modal-endTime">Hora Fin *</label>
                    <select
                      id="modal-endTime"
                      value={calendarFormData.endTime}
                      onChange={(e) => setCalendarFormData({ ...calendarFormData, endTime: e.target.value })}
                      required
                    >
                      <option value="">Fin</option>
                      {['7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tipo de Ubicación *</label>
                  <div style={{ display: 'flex', gap: '15px', marginTop: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="modal-locationType"
                        value="physical"
                        checked={calendarFormData.locationType === 'physical'}
                        onChange={() => setCalendarFormData({ ...calendarFormData, locationType: 'physical' })}
                      />
                      Física (Edificio/Aula)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="modal-locationType"
                        value="virtual"
                        checked={calendarFormData.locationType === 'virtual'}
                        onChange={() => setCalendarFormData({ ...calendarFormData, locationType: 'virtual' })}
                      />
                      Virtual (Enlace/Tema)
                    </label>
                  </div>
                </div>

                {calendarFormData.locationType === 'physical' ? (
                  <div className="form-row" style={{ display: 'flex', gap: '10px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label htmlFor="modal-building">Edificio *</label>
                      <select
                        id="modal-building"
                        value={calendarFormData.building}
                        onChange={(e) => setCalendarFormData({ ...calendarFormData, building: e.target.value })}
                        required
                      >
                        <option value="">Selecciona</option>
                        <option value="1">Edificio 1</option>
                        <option value="2">Edificio 2</option>
                        <option value="3">Edificio 3</option>
                        <option value="4">Edificio 4</option>
                        <option value="5">Edificio 5</option>
                        <option value="6">Edificio 6</option>
                        <option value="Bungalow">Bungalow</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label htmlFor="modal-classroom">Aula *</label>
                      <input
                        type="text"
                        id="modal-classroom"
                        value={calendarFormData.classroom}
                        placeholder="Ej: 307"
                        onChange={(e) => setCalendarFormData({ ...calendarFormData, classroom: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label htmlFor="modal-meetingLink">Enlace de Reunión *</label>
                      <input
                        type="url"
                        id="modal-meetingLink"
                        value={calendarFormData.meetingLink}
                        placeholder="https://meet.google.com/..."
                        onChange={(e) => setCalendarFormData({ ...calendarFormData, meetingLink: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="modal-topic">Tema / Sílabo *</label>
                      <input
                        type="text"
                        id="modal-topic"
                        value={calendarFormData.topic}
                        placeholder="Ej: Repaso de ecuaciones"
                        onChange={(e) => setCalendarFormData({ ...calendarFormData, topic: e.target.value })}
                        required
                      />
                    </div>
                  </>
                )}

                <div className="form-actions" style={{ border: 'none', paddingTop: '15px', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setShowCalendarFormModal(false)}
                    style={{ margin: 0 }}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="save-button" style={{ margin: 0 }}>
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Moderación de Roles por Administrador */}
      {showModerationModal && moderatingUser && (
        <div className="modal-overlay" onClick={() => setShowModerationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>Moderar Usuario</h2>
              <button
                className="modal-close"
                onClick={() => setShowModerationModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{moderatingUser.fullName}</div>
                <div style={{ fontSize: '0.9rem', color: '#718096' }}>{moderatingUser.email}</div>
              </div>

              {moderationError && (
                <div className="error-message" style={{ marginBottom: '15px' }}>
                  {moderationError}
                </div>
              )}

              <form onSubmit={handleModerationSubmit} style={{ padding: 0, boxShadow: 'none', background: 'none' }}>
                <div className="form-group">
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Roles de Tutor *</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', background: '#f8f9fa', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={moderationFormData.tutorRoles.includes('vtic')}
                        onChange={() => handleModerationRoleToggle('vtic')}
                      />
                      Lucem Aspicio
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={moderationFormData.tutorRoles.includes('aurora')}
                        onChange={() => handleModerationRoleToggle('aurora')}
                      />
                      Aurora
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={moderationFormData.tutorRoles.includes('phuma')}
                        onChange={() => handleModerationRoleToggle('phuma')}
                      />
                      Phuma
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={moderationFormData.tutorRoles.includes('eucs')}
                        onChange={() => handleModerationRoleToggle('eucs')}
                      />
                      EUCS
                    </label>
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    <input
                      type="checkbox"
                      checked={moderationFormData.isAdmin}
                      onChange={(e) => setModerationFormData({ ...moderationFormData, isAdmin: e.target.checked })}
                    />
                    ¿Es Administrador de la Plataforma?
                  </label>
                  <p style={{ fontSize: '0.8rem', color: '#718096', margin: '4px 0 0 22px' }}>
                    Otorga permisos globales para cambiar roles, ver accesos e información de todos los usuarios y editar el calendario.
                  </p>
                </div>

                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    <input
                      type="checkbox"
                      checked={moderationFormData.isAccredited}
                      onChange={(e) => setModerationFormData({ ...moderationFormData, isAccredited: e.target.checked })}
                    />
                    ¿Es Tutor Acreditado?
                  </label>
                  <p style={{ fontSize: '0.8rem', color: '#718096', margin: '4px 0 0 22px' }}>
                    Destaca al tutor con una etiqueta especial llamativa y lo posiciona siempre al principio de la lista de tutores.
                  </p>
                </div>

                <div className="form-group" style={{ marginTop: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#b45309' }}>
                    <input
                      type="checkbox"
                      checked={moderationFormData.isRetired}
                      onChange={(e) => setModerationFormData({ ...moderationFormData, isRetired: e.target.checked })}
                    />
                    ¿Es Tutor Retirado (Cuadro de Honor)?
                  </label>
                  <p style={{ fontSize: '0.8rem', color: '#718096', margin: '4px 0 0 22px' }}>
                    Si se marca, el tutor ya no aparecerá en el catálogo activo y se mostrará exclusivamente en el Mural de Retiro con su Cuadro de Honor.
                  </p>

                  {moderationFormData.isRetired && (
                    <div style={{ marginTop: '12px', paddingLeft: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Fecha de Inicio *</label>
                          <input
                            type="date"
                            value={moderationFormData.retiredData?.startDate || ''}
                            onChange={(e) => setModerationFormData({
                              ...moderationFormData,
                              retiredData: { ...moderationFormData.retiredData, startDate: e.target.value }
                            })}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            required={moderationFormData.isRetired}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Fecha de Retiro *</label>
                          <input
                            type="date"
                            value={moderationFormData.retiredData?.endDate || ''}
                            onChange={(e) => setModerationFormData({
                              ...moderationFormData,
                              retiredData: { ...moderationFormData.retiredData, endDate: e.target.value }
                            })}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            required={moderationFormData.isRetired}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Bibliografía de Retiro / Reconocimiento</label>
                        <textarea
                          placeholder="Escribe un breve texto o reconocimiento sobre su retiro del voluntariado..."
                          value={moderationFormData.retiredData?.honorBio || ''}
                          onChange={(e) => setModerationFormData({
                            ...moderationFormData,
                            retiredData: { ...moderationFormData.retiredData, honorBio: e.target.value }
                          })}
                          rows="4"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.9rem' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Zona de Peligro: Deshabilitar y Borrar Cuentas */}
                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#e53e3e', fontSize: '0.9rem' }}>
                    Zona de Peligro (Administración de Cuenta)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: '#718096', margin: '0 0 10px 0' }}>
                    Deshabilitar bloquea el inicio de sesión inmediatamente. Eliminar borra el perfil permanentemente de Firestore.
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      style={{
                        padding: '8px 14px',
                        backgroundColor: moderatingUser.isDisabled ? '#38a169' : '#d69e2e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        transition: 'opacity 0.2s'
                      }}
                      onClick={async () => {
                        const statusText = moderatingUser.isDisabled ? 'habilitar' : 'deshabilitar'
                        if (window.confirm(`¿Estás seguro de que deseas ${statusText} la cuenta de ${moderatingUser.fullName || moderatingUser.email}?`)) {
                          setModerationSaving(true)
                          try {
                            const res = await disableUserAccount(moderatingUser.id, !moderatingUser.isDisabled)
                            if (res.success) {
                              setShowModerationModal(false)
                              await loadCourses()
                            } else {
                              setModerationError(res.error || 'Error al cambiar estado de cuenta')
                            }
                          } catch (err) {
                            setModerationError(err.message)
                          } finally {
                            setModerationSaving(false)
                          }
                        }
                      }}
                      disabled={moderationSaving}
                    >
                      {moderatingUser.isDisabled ? 'Habilitar Cuenta' : 'Deshabilitar Cuenta'}
                    </button>
                    <button
                      type="button"
                      style={{
                        padding: '8px 14px',
                        backgroundColor: '#e53e3e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        transition: 'opacity 0.2s'
                      }}
                      onClick={async () => {
                        if (window.confirm(`⚠️ ADVERTENCIA CRÍTICA: ¿Estás completamente seguro de que deseas ELIMINAR la cuenta de ${moderatingUser.fullName || moderatingUser.email}? Esta acción no se puede deshacer y eliminará permanentemente su perfil de tutor de la base de datos.`)) {
                          setModerationSaving(true)
                          try {
                            const res = await deleteUserAccount(moderatingUser.id)
                            if (res.success) {
                              setShowModerationModal(false)
                              await loadCourses()
                            } else {
                              setModerationError(res.error || 'Error al eliminar la cuenta')
                            }
                          } catch (err) {
                            setModerationError(err.message)
                          } finally {
                            setModerationSaving(false)
                          }
                        }
                      }}
                      disabled={moderationSaving}
                    >
                      Eliminar Cuenta
                    </button>
                  </div>
                </div>

                <div className="form-actions" style={{ border: 'none', paddingTop: '20px', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setShowModerationModal(false)}
                    style={{ margin: 0 }}
                    disabled={moderationSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="save-button"
                    style={{ margin: 0 }}
                    disabled={moderationSaving}
                  >
                    {moderationSaving ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard

